import { getUserSubject } from '../../../services/auth';
import { ErrorNotification } from '../../../services/error-notification';
import { ResultsFromSource, SearchApi, SearchResult } from '../search-api';

export type GlobalSearchStatus = 'idle' | 'loading' | 'loaded' | 'error';

export interface ProviderSearchResults extends ResultsFromSource {
  status: GlobalSearchStatus;
}

export type SearchOuterTabKeys = 'documentation' | 'not-documentation';

// Should match search endpoint providers
export type SearchProviderNames =
  | 'dashboards'
  | 'applications'
  | 'tasks'
  | 'instances'
  | 'resources'
  | 'fx'
  | 'springboard-file'
  | 'documentation';

export function getSearchModelStorageKey() {
  return `ZoeGlobalSearchModel;version=2;userSubject=${getUserSubject() ?? ''}`;
}

// !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
// !! IMPORTANT: increment the version in SEARCH_MODEL_STORAGE_KEY when making any change here !!
// !! or to the api response.                                                                  !!
// !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
export interface GlobalSearchModel {
  /**
   * The value of the input box,.
   */
  inputValue: string;

  /**
   * The value we've searched for.
   */
  searchTerm: string;

  /**
   * Search results. These are grouped into the source of the result e.g. Applications, Jobs, Instances etc.
   */
  searchResults: Record<string, ProviderSearchResults>;

  /**
   * The current status of the search process.
   */
  status: GlobalSearchStatus;

  /**
   * A list of all available search providers.
   */
  defaultSearchProviders: SearchProviderNames[];

  /**
   * Selected tab name in results  global search results
   */
  selectedTab: SearchProviderNames;

  /**
   * Selected outer tab name in global search results
   */
  selectedOuterModalTab: SearchOuterTabKeys;
}

const DEFAULT_TAB: SearchProviderNames = 'documentation';
const DEFAULT_OUTER_TAB: SearchOuterTabKeys = 'documentation';

export function pack(
  model: GlobalSearchModel,
  providerName: SearchProviderNames,
  results: Error | SearchResult[] | undefined,
  firstTabWithSomeResult?: SearchProviderNames
): GlobalSearchModel {
  const error = results instanceof Error;

  const output = {
    ...model,
    searchResults: {
      ...model.searchResults,
      [providerName]: {
        label: providerName,
        status: error ? 'error' : 'loaded',
        searchResults: !error ? results : [],
      } as ProviderSearchResults,
    },
  };

  output.selectedTab = firstTabWithSomeResult ?? DEFAULT_TAB;

  if (!firstTabWithSomeResult || firstTabWithSomeResult === DEFAULT_OUTER_TAB) {
    output.selectedOuterModalTab = DEFAULT_OUTER_TAB;
  } else {
    output.selectedOuterModalTab = 'not-documentation';
  }

  return output;
}

export function initialGlobalSearchResults(): GlobalSearchModel {
  return {
    inputValue: '',
    searchTerm: '',
    searchResults: {},
    status: 'idle',
    defaultSearchProviders: window.CONFIG.searchProviders as SearchProviderNames[],
    selectedTab: DEFAULT_TAB,
    selectedOuterModalTab: DEFAULT_OUTER_TAB,
  };
}

export function createDefaultResults(providerNames: SearchProviderNames[], status: string): Record<string, ProviderSearchResults> {
  return providerNames.reduce((acc, cur) => {
    return {
      ...acc,
      [cur]: {
        status,
        label: cur,
        searchResults: [],
      } as ProviderSearchResults,
    };
  }, {});
}

export async function searchGlobal(
  api: SearchApi,
  getModel: () => GlobalSearchModel,
  update: (model: GlobalSearchModel) => void
): Promise<ErrorNotification | undefined> {
  const model = getModel();
  if (!model) {
    return;
  }

  const searchTerm = model.inputValue?.trim();
  if (!searchTerm) {
    update({
      ...model,
      inputValue: '',
      searchTerm: '',
      searchResults: createDefaultResults(model.defaultSearchProviders, 'ready'),
      status: 'idle',
    });
    return;
  }

  update({
    ...getModel(),
    searchTerm,
    status: 'loading',
    searchResults: createDefaultResults(model.defaultSearchProviders, 'loading'),
  });

  // request to all search providers
  const resultsWithProviders = await Promise.all(
    model.defaultSearchProviders.map(async (providerName) => {
      try {
        const results = await api.getOptionsForTerm(providerName, searchTerm);
        return { providerName, results: results?.searchResults || [] };
      } catch (err) {
        return { providerName, results: [], error: err as Error };
      }
    })
  );

  const providersWithResults = resultsWithProviders
    .filter((provider) => provider.results.length > 0)
    .map((provider) => provider.providerName);

  // If the default tab has results, activate it, otherwise activate the first tab with results
  const firstTabWithSomeResult = providersWithResults.includes(DEFAULT_TAB) ? DEFAULT_TAB : providersWithResults[0];

  resultsWithProviders.forEach(({ providerName, results, error }) => {
    if (error) {
      update(pack(getModel(), providerName, error));
    } else {
      update(pack(getModel(), providerName, results, firstTabWithSomeResult));
    }
  });

  update({ ...getModel(), status: 'loaded' });
}
